import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { showPopup } from "../../../src/containers/common/commonSlice";
import { setIsLoading } from "../../../src/containers/common/commonSlice";
import moment from "moment";
import skillsOptionsRaw from "./skills.json"
import { Localize } from "../../app/lang/langs"
import { compareCollabsByOrder } from "../../app/utils/helpers"

const initialState = {
  list: [],
  currentList: [],
  // Profile-picture editor (own picture only — see CollabPhotoController on the backend).
  photoEditorOpen: false,
  myPhotoSaving: false,
  myPhotoError: null,
  updateList: [],
  id_groupe: null,
  collaboratorsList: [],
  allCollabsList: [],
  allCollabsLoader: 'loading',
  isLoadingCra: false,
  zipGenerationProgress: null,
  collabModal: {
    show: false
  },
  resetPasswordModal: false,
  resetPasswordResponse: null,
  managers: [],
  warningsList: [],
  selectedView: 0,
  warningDialog: false,
  warningData: null,
  warningDeleteRes: null,
  warningEditRes: null,
  warningUpdateBody: null,
  addWarningDialog: false,
  addWarningRes: null,
  addedWarningBody: null,
  warningCatgories: [
    { value: "warning", color: "#f59e0b" },
    { value: "recall the order", color: "#e74c3c" },
    { value: "formal notice", color: "#0ea5e9" },
    { value: "Demande rupture conventionnelle", color: "#1e8449" },
    { value: "Demande prime", color: "#6c3483" },
    { value: "Demande augmentation salaire", color: "#e67e22" },
    { value: "Mise à pied", color: "#892929ff" },
    { value: "Mutation disciplinaire", color: "#2b6058ff" },
    { value: "Rétrogradation disciplinaire", color: "#69279eff" }
  ],
  specialCasesOptions: [
    { label: "Arrêt maladie longue durée", value: "Arrêt maladie longue durée" },
    { label: "Instance de départ", value: "Instance de départ" },
    { label: "Congé maternité", value: "Congé maternité" },
    { label: "Congé paternité", value: "Congé paternité" },
    { label: "Contrat suspendu", value: "Contrat suspendu" },
    { label: "Mi-temps thérapeutique", value: "Mi-temps thérapeutique" },
    { label: "Congé parental", value: "Congé parental" }
  ],
  companies: [],
  singleCollabDetails: null,
  singleCollabProfile: null,
  postes: [],
  collabContract: null,
  specialCases: [],
  specialCaseModalData: null,
  displaySpecialCaseModalDetails: false,
  skillsOptions: skillsOptionsRaw.map(option => {
    return { value: option, label: option }
  }),
  singleCollabManagedTeams: [],
  allContracts: [],
  allMissionsFollowups: []
}

/**
 * Writes a new `photoUpdatedAt` onto one collab wherever they appear.
 *
 * The same collab is held in several lists built by different endpoints (`list` and
 * `currentList` from getCollabs, `collaboratorsList` from getMembers, `allCollabsList` from
 * getAllCollabs). Missing one leaves a stale face on whichever screen reads it.
 */
const applyPhotoUpdatedAt = (state, idCollab, photoUpdatedAt) => {
  if (!idCollab) return
  const lists = [state.list, state.currentList, state.collaboratorsList, state.allCollabsList]
  lists.forEach(list => {
    if (!Array.isArray(list)) return
    list.forEach(collab => {
      if (collab && collab.id == idCollab) {
        collab.photoUpdatedAt = photoUpdatedAt
      }
    })
  })
  if (state.singleCollabProfile && state.singleCollabProfile.id == idCollab) {
    state.singleCollabProfile.photoUpdatedAt = photoUpdatedAt
  }
  // The detail modal holds its own copy of the row rather than reading one of the lists,
  // so without this its header keeps showing the old face while the grid behind it has
  // already updated.
  if (state.collabModal && state.collabModal.id == idCollab) {
    state.collabModal.photoUpdatedAt = photoUpdatedAt
  }
}

/**
 * The backend's own message when it explains itself (a 422 carries the specific reason the
 * image was refused — wrong format, not square, too large), falling back to a generic
 * string for a 500 or a dropped connection.
 */
const photoErrorMessage = (payload) => {
  const responseError = payload && payload.responseError
  if (responseError && responseError.data && responseError.data.error) {
    return responseError.data.error
  }
  return Localize('PHOTO_ERROR_SAVE')
}

export const collabsSlice = createSlice({
  name: "collabs",
  initialState,
  reducers: {
    setCollabs: (state, action) => {
      state.list = action.payload
    },
    setPhotoEditorOpen: (state, action) => {
      state.photoEditorOpen = action.payload
      // Closing clears the last failure, so reopening does not show a stale error.
      if (!action.payload) state.myPhotoError = null
    },
    setMyPhotoError: (state, action) => {
      state.myPhotoError = action.payload
    },
    setCurrentCollabs: (state, action) => {
      state.currentList = action.payload
    },
    setWarningDeleteRes: (state, action) => {
      state.warningDeleteRes = action.payload
    },
    setWarningEditRes: (state, action) => {
      state.warningEditRes = action.payload
    },
    setAddWarningRes: (state, action) => {
      state.addWarningRes = action.payload
    },
    setAddedWarningBody: (state, action) => {
      state.addedWarningBody = action.payload
    },
    setWarningUpdateBody: (state, action) => {
      state.warningUpdateBody = action.payload
    },
    setSelectedView: (state, action) => {
      state.selectedView = action.payload
    },
    setAddWarningDialog: (state, action) => {
      state.addWarningDialog = action.payload
    },
    setCollabModalData: (state, action) => {
      state.collabModal = action.payload
    },
    setUpdateTeamPermissionsRes: (state) => {
      state.updateTeamPermissionsRes = null
    },
    setUpdatePermissionsRes: (state) => {
      state.updatePermissionsRes = null
    },
    showCollabModal: (state, action) => {
      state.collabModal.show = action.payload
    },
    setResetPasswordModal: (state, action) => {
      state.resetPasswordModal = action.payload
    },
    setWarningDetailsDialog: (state, action) => {
      state.warningDialog = action.payload
    },
    setWarningData: (state, action) => {
      state.warningData = action.payload
    },
    setResetPasswordResponse: (state) => {
      state.resetPasswordResponse = null;
    },
    setUpdatedCurrentCollabs: (state, action) => {
      state.updateList = action.payload
      let updatedList = state.currentList.reduce((array, current) => {
        let collab = Object.assign({}, current)
        let newCollabs = state.updateList.filter(c => c.id == current.id)
        if (newCollabs.length) {
          Object.keys(newCollabs[0]).map(key => {
            collab[key] = newCollabs[0][key]
          })
        }
        collab["solde"] = collab["solde_cp_current"] + collab["solde_cp_old"]
        array.push(collab)
        return array
      }, [])
      state.currentList = updatedList
    },
    setCollabsGroupeId: (state, action) => {
      state.id_groupe = action.payload;
    },
    // addCollab: (state, action) => {
    //   state.push({
    //     value: generateUniqueId(),
    //     label: action.payload.name,
    //     email: action.payload.email,
    //     groupeIds: [],
    //   });
    // },
    addCollabToGroupe: (state, action) => {
      if (action.payload.id && action.payload.id_groupe) {
        let collabs = state.list.filter(
          (collab) => collab.value === action.payload.id
        )
        if (collabs.length) {
          let collab = collabs[0]
          collab.groupeIds = [action.payload.id_groupe]
        }
      }
    },
    removeCollabFromGroupe: (state, action) => {
      if (action.payload.id && action.payload.id_groupe) {
        let collabs = state.filter((collab) => collab.id === action.payload.id);
        if (collabs.length) {
          let collab = collabs[0];
          const index = collab.groupeIds.indexOf(action.payload.id_groupe);
          if (index > -1) {
            collab.groupeIds.splice(index, 1);
          }
        }
      }
    }, setIsLoadingCra: (state, action) => {
      state.isLoadingCra = action.payload
    },
    addNewlyEvaluated: (state, action) => {
      const [id, numberOfEvaluations] = action.payload
      if (numberOfEvaluations) {
        state.list = state.list.map(item => {
          if (item.id === id) {
            return {
              ...item,
              evaluated: item.evaluated + numberOfEvaluations
            };
          }
          return item
        })
      }
    },
    setZipGenerationProgress(state, action) {
      state.zipGenerationProgress = action.payload;
    },
    setCollabContract(state, action) {
      state.collabContract = action.payload;
    },
    setSpecialCases(state, action) {
      state.specialCases = action.payload;
    },
    setSpecialCaseModalData(state, action) {
      state.specialCaseModalData = action.payload;
    },
    setDisplaySpecialCaseModalDetails(state, action) {
      state.displaySpecialCaseModalDetails = action.payload;
    },
    setSingleCollabProfile: (state, action) => {
      state.singleCollabProfile = action.payload;
    },
    setSingleCollabManagedTeams: (state, action) => {
      state.singleCollabManagedTeams = action.payload;
    },
    updateCollabSuiviStatus: (state, action) => {
      // update last review state in collabs table
      let followup = action.payload
      if (followup && followup.id_collab) {
        state.currentList = state.currentList.map(collab => {
          if (collab.id == followup.id_collab) {
            return { ...collab, statut_suivi: followup.status }
          }
          return { ...collab }
        })
        
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCollabs.fulfilled, (state, action) => {

      let teamCollabsIds = {}
      if (
        action.payload &&
        action.payload.responseData &&
        action.payload.responseData["collabs"]
      ) {
        let collabsList = []

        for (const key in action.payload.responseData["collabs"]) {
          let collab = action.payload.responseData["collabs"][key]
          if (!collab) continue
          let data = {
            value: collab["id"],
            label: `${collab["prenom"]} ${collab["nom"]}`,
            nom: collab["nom"],
            prenom: collab["prenom"],
            mobileNumber: collab["mobile_number"],
            security_number: collab["security_number"] || '',
            email: collab["email"],
            matricule: collab["matricule"],
            company: collab["company"],
            id_company: collab["id_company"],
            poste: collab["poste"],
            order: collab["order"],
            id: collab["id"],
            groupeIds: [state.id_groupe],
            id_team: collab["id_team"],
            id_country: collab["id_country"],
            statut: collab["statut"],
            entryDate: collab["entree_date"],
            ancienneteeDate: collab["anciennetee_date"] || collab["entree_date"],
            exitDate: collab["sortie_date"],
            color: collab["color"],
            // Flag, not the image. CollabAvatar turns it into a cacheable <img src>; null
            // means no picture and it renders initials without a request.
            photoUpdatedAt: collab["photo_updated_at"],
            auto: collab["auto"],
            localization: collab["localization"],
            evaluated: collab["evaluated"],
            totalEvaluationFields: collab["totalEvaluationFields"],
            entree_date_team: collab["start_date"],
            sortie_date_team: collab["end_date"],
            team: collab["team"],
            type_collab: collab["type_collab"],
          }
          if (collab["type_collab"] == 'interne' || collab["id_country"] != 1) {
            data["solde_cp_current"] = Number(collab["solde_cp_current"])
            data["solde_cp_old"] = Number(collab["solde_cp_old"])
            data["solde_cp_old_expiry"] = collab["solde_cp_old_expiry"]
            data["solde"] = Number((Number(collab["solde_cp_current"]) + Number(collab["solde_cp_old"])).toFixed(2))
          }
          if (collab["type_collab"] == 'interne' && collab["id_country"] == 1) {
            data["solde_cp_current_remaining"] = Number(collab["solde_cp_current_remaining"])
            data["solde_cp_old_remaining"] = Number(collab["solde_cp_old_remaining"])
          }
          if (collab["solde_sick_leaves"] != null) {
            data["solde_sick_leaves"] = Number(collab["solde_sick_leaves"])
          }
          if (collab["solde_extra_leaves"] != null) {
            data["solde_extra_leaves"] = Number(collab["solde_extra_leaves"])
          }

          if (collab["id_team_delegate"])
            data.id_team_delegate = collab["id_team_delegate"]

          if (collab["statut_suivi"])
            data.statut_suivi = collab["statut_suivi"]

          collabsList.push(data)

          if (teamCollabsIds[collab.id_team]) {
            teamCollabsIds[collab.id_team].push(collab.id)
          } else {
            teamCollabsIds[collab.id_team] = [collab.id]
          }

        }
        let sortedList = collabsList.sort(compareCollabsByOrder)
        // check if it's this month's data
        if (action.payload.body.isCurrent) {
          state.currentList = sortedList
        } else {
          state.list = sortedList
        }
      }
      if (
        action.payload.responseData &&
        action.payload.responseData["managers"]
      ) {
        state.managers = []
        action.payload.responseData["managers"].map(manager => {
          if (teamCollabsIds[manager.id_team] && teamCollabsIds[manager.id_team].includes(manager.id)) {
            manager.isTeamCollab = true
          }
          state.managers.push(manager)
        })
      }
    });
    builder.addCase(getMembers.fulfilled, (state, action) => {
      if (
        action.payload.responseData &&
        action.payload.responseData["groupes"]
      ) {
        let collaboratorsMap = {}
        for (const key in action.payload.responseData["groupes"]) {
          let groupe = action.payload.responseData["groupes"][key];
          for (const collabKey in groupe["collabs"]) {
            let collab = groupe["collabs"][collabKey]
            if (!collaboratorsMap[collab["id"]]) {
              collaboratorsMap[collab["id"]] = {
                value: collab["id"],
                label: `${collab["prenom"]} ${collab["nom"]}`,
                nom: collab["nom"],
                id: collab["id"],
                email: collab["email"],
                prenom: collab["prenom"],
                matricule: collab["matricule"],
                id_silae: collab["id_silae"],
                company: collab["company"],
                mobileNumber: collab["mobile_number"],
                poste: collab["poste"],
                order: collab["order"],
                groupeIds: [],
                statut: collab["statut"],
                id_team: groupe["id_team"],
                entryDate: collab["entree_date"],
                ancienneteeDate: collab["anciennetee_date"] || collab["entree_date"],
                exitDate: collab["sortie_date"],
                color: collab["color"],
                photoUpdatedAt: collab["photo_updated_at"],
                auto: collab["auto"]
              }
            }
            collaboratorsMap[collab["id"]].groupeIds.push(groupe["id"])
          }
        }
        state.collaboratorsList = Object.values(collaboratorsMap).sort(compareCollabsByOrder)
      }
    })
    //Try to add it locally
    builder.addCase(addGroupeMembers.fulfilled, (state, action) => {
      if (action.payload.responseData) {
      }
    })
    //Try to remove it locally
    builder.addCase(removeGroupeMembers.fulfilled, (state, action) => {
      if (action.payload.responseData) {
      }
    })
    builder.addCase(updateCollabs.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.updateList = []
      }
    })
    builder.addCase(editCollab.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.updateList = []
      }
    })

    // ── Own profile picture ───────────────────────────────────────────────────
    //
    // On success the new `photo_updated_at` is written into every already-loaded list
    // instead of refetching them. That timestamp is what CollabAvatar builds its URL from,
    // so patching it in place is what makes the new face appear immediately — a refetch of
    // getAllCollabs to learn one timestamp would be far more expensive.
    builder.addCase(setMyPhoto.pending, (state) => {
      state.myPhotoSaving = true
      state.myPhotoError = null
    })
    builder.addCase(setMyPhoto.fulfilled, (state, action) => {
      state.myPhotoSaving = false
      const data = action.payload && action.payload.responseData
      if (data && data.status == 1) {
        applyPhotoUpdatedAt(state, action.payload.idCollab, data.photo_updated_at)
        state.photoEditorOpen = false
      } else {
        state.myPhotoError = photoErrorMessage(action.payload)
      }
    })
    builder.addCase(setMyPhoto.rejected, (state, action) => {
      state.myPhotoSaving = false
      state.myPhotoError = photoErrorMessage(action.payload)
    })
    builder.addCase(setCollabPhoto.pending, (state) => {
      state.myPhotoSaving = true
      state.myPhotoError = null
    })
    builder.addCase(setCollabPhoto.fulfilled, (state, action) => {
      state.myPhotoSaving = false
      const data = action.payload && action.payload.responseData
      if (data && data.status == 1) {
        // Same patch-in-place as setMyPhoto: the new stamp is what makes the face appear
        // in the grid without refetching getAllCollabs.
        applyPhotoUpdatedAt(state, action.payload.idCollab, data.photo_updated_at)
      } else {
        state.myPhotoError = photoErrorMessage(action.payload)
      }
    })
    builder.addCase(setCollabPhoto.rejected, (state, action) => {
      state.myPhotoSaving = false
      state.myPhotoError = photoErrorMessage(action.payload)
    })
    builder.addCase(removeCollabPhoto.pending, (state) => {
      state.myPhotoSaving = true
      state.myPhotoError = null
    })
    builder.addCase(removeCollabPhoto.fulfilled, (state, action) => {
      state.myPhotoSaving = false
      const data = action.payload && action.payload.responseData
      if (data && data.status == 1) {
        // Patches every loaded list, so the avatar in the grid falls back to initials
        // without refetching getAllCollabs.
        applyPhotoUpdatedAt(state, action.payload.idCollab, null)
      } else {
        state.myPhotoError = photoErrorMessage(action.payload)
      }
    })
    builder.addCase(removeCollabPhoto.rejected, (state, action) => {
      state.myPhotoSaving = false
      state.myPhotoError = photoErrorMessage(action.payload)
    })
    builder.addCase(removeMyPhoto.pending, (state) => {
      state.myPhotoSaving = true
      state.myPhotoError = null
    })
    builder.addCase(removeMyPhoto.fulfilled, (state, action) => {
      state.myPhotoSaving = false
      const data = action.payload && action.payload.responseData
      if (data && data.status == 1) {
        // null is meaningful: it is what makes the avatar fall back to initials.
        applyPhotoUpdatedAt(state, action.payload.idCollab, null)
        state.photoEditorOpen = false
      } else {
        state.myPhotoError = photoErrorMessage(action.payload)
      }
    })
    builder.addCase(removeMyPhoto.rejected, (state, action) => {
      state.myPhotoSaving = false
      state.myPhotoError = photoErrorMessage(action.payload)
    })
    builder.addCase(getAllCollabs.fulfilled, (state, action) => {
      if (
        action.payload.responseData &&
        action.payload.responseData["allCollabs"]
      ) {
        let collabsList = [];

        for (const key in action.payload.responseData["allCollabs"]) {

          let collab = action.payload.responseData["allCollabs"][key]

          // if no current contract, use last contract data
          if (!collab["contract_id"]) {
            collab[`silae_status`] = collab[`last_contract_silae_status`]
            collab[`motif_sortie`] = collab[`last_contract_motif_sortie`]
            collab[`periode_essai_1`] = collab[`last_contract_periode_essai_1`]
            collab[`periode_essai_2`] = collab[`last_contract_periode_essai_2`]
            collab[`tjm_achat`] = collab[`last_contract_tjm_achat`]
            collab[`tjm_vente`] = collab[`last_contract_tjm_vente`]
            collab[`id_company`] = collab[`last_contract_id_company`]
            collab[`entree_date`] = collab[`last_contract_entree_date`]
            collab[`sortie_date`] = collab[`last_contract_sortie_date`]
            collab[`date_debut_cp`] = collab[`last_contract_date_debut_cp`]
            collab[`date_fin_cp`] = collab[`last_contract_date_fin_cp`]
            collab[`new_date_debut_cp`] = collab[`last_contract_new_date_debut_cp`]
            collab[`silae_status`] = collab[`last_contract_silae_status`]
            collab[`type_collab`] = collab[`last_contract_type_collab`]
            collab[`nature_emploi`] = collab[`last_contract_nature_emploi`]
            collab[`statut_emploi`] = collab[`last_contract_statut_emploi`]
            collab[`position`] = collab[`last_contract_position`]
            collab[`coefficient`] = collab[`last_contract_coefficient`]
            collab[`etablissement`] = collab[`last_contract_etablissement`]
            collab[`company`] = collab[`last_company`]
            collab[`current_poste`] = collab[`last_poste`]
            collab[`current_localization`] = collab[`last_localization`]
            collab[`current_id_team`] = collab[`last_id_team`]
            collab[`current_team_name`] = collab[`last_team_name`]
            collab[`type_contrat`] = collab[`last_contract_type_contrat`]
            collab[`comments`] = collab[`last_contract_comments`]
            collab[`subcontractor`] = collab["last_contract_subcontractor"]
            collab[`subcontractor_email`] = collab["last_contract_subcontractor_email"]?.trim() || ''
          }

          let entryDateTimestamp = collab["entree_date"] ? moment(collab["entree_date"]).toDate() : null;
          let exitDateTimestamp = collab["sortie_date"] ? moment(collab["sortie_date"]).toDate() : null;
          let periodeEssai1 = collab["periode_essai_1"] ? moment(collab["periode_essai_1"]).toDate() : null;
          let periodeEssai2 = collab["periode_essai_2"] ? moment(collab["periode_essai_2"]).toDate() : null;
          let ancienneteeDateTimestamp = collab["anciennetee_date"]
            ? moment(collab["anciennetee_date"]).toDate()
            : moment(collab["entree_date"]).toDate()
          let soldeCpOldExpiryTimestamp = collab["solde_cp_old_expiry"] ? moment(collab["solde_cp_old_expiry"]).toDate() : null;
          let createdAtTimestamp = collab["created_at"] ? moment(collab["created_at"]).toDate() : null;
          let dateDebutCPTimestamp = collab["date_debut_cp"] ? moment(collab["date_debut_cp"]).toDate() : null;
          let dateFinCPTimestamp = collab["date_fin_cp"] ? moment(collab["date_fin_cp"]).toDate() : null;
          let dateFinNewCPTimestamp = collab["new_date_debut_cp"] ? moment(collab["new_date_debut_cp"]).toDate() : null;//NAMING IS WRONG, IT'S NEW DATE FIN CP
          let firstExperienceTimestamp = collab["first_experience_date"] ? moment(collab["first_experience_date"]).toDate() : null;
          let lastContractEntreeDateTimestamp = collab["last_contract_entree_date"] ? moment(collab["last_contract_entree_date"]).toDate() : null;
          let lastContractSortieDateTimestamp = collab["last_contract_sortie_date"] ? moment(collab["last_contract_sortie_date"]).toDate() : null;
          let titreDeSejourEndDate = collab["date_fin_titre_sejour"] ? moment(collab["date_fin_titre_sejour"]).toDate() : null;

          let data = {
            value: collab["id"],
            label: `${collab["prenom"]?.trim() || ''} ${collab["nom"]?.trim() || ''}`,
            nom: collab["nom"]?.trim() || '',
            prenom: collab["prenom"]?.trim() || '',
            date_naissance: collab["date_naissance"] || '',
            title: collab["title"],
            prime_mission: collab["prime_mission"],
            is_new: collab["is_new"],
            mobileNumber: collab["mobile_number"]?.trim() || '',
            security_number: collab["security_number"] || '',
            email: collab["email"]?.trim() || '',
            matricule: collab["matricule"]?.trim() || '',
            company: collab["company"]?.trim() || '',
            id_company: collab["id_company"],
            poste: collab["poste"]?.trim() || '',
            order: collab["order"],
            id: collab["id"],
            silaeStatus: collab["silae_status"] || "N/A",
            groupeIds: [state.id_groupe],
            id_team: collab["id_team"],
            statut: collab["is_new"] ? "New" : (collab["statut"]?.trim() || ''),
            entryDate: entryDateTimestamp,
            periodeEssai1,
            periodeEssai2,
            ancienneteeDate: ancienneteeDateTimestamp,
            exitDate: exitDateTimestamp,
            color: collab["color"]?.trim() || '',
            auto: collab["auto"],
            localization: collab["localization"]?.trim() || '',
            team: collab["team_name"]?.trim() || '', // ← current team
            typeLogin: collab["type_login"]?.trim() || '',
            solde_sick_leaves: Number(collab["solde_sick_leaves"]),
            solde_extra_leaves: Number(collab["solde_extra_leaves"]),
            solde_cp_current: Number(collab["solde_cp_current"]),
            solde_cp_old: Number(collab["solde_cp_old"]),
            solde_cp_old_expiry: soldeCpOldExpiryTimestamp,
            solde_cp_old_remaining: Number(collab["solde_cp_old_remaining"]),
            solde_cp_current_remaining: Number(collab["solde_cp_current_remaining"]),
            solde: Number((Number(collab["solde_cp_current"]) + Number(collab["solde_cp_old"])).toFixed(2)),
            forfait_deplacement: collab["forfait_deplacement"],
            frais_tel: collab["frais_tel"],
            type_collab: collab["type_collab"]?.trim() || 'None',
            createdAt: createdAtTimestamp,
            date_debut_cp: dateDebutCPTimestamp,
            date_fin_cp: dateFinCPTimestamp,
            new_date_fin_cp: dateFinNewCPTimestamp,
            is_handicap: collab["is_handicap"],
            type_contrat: collab["type_contrat"],
            tjm_achat: collab["tjm_achat"],
            tjm_vente: collab["tjm_vente"],
            subcontractor: collab["subcontractor"],
            subcontractor_email: collab["subcontractor_email"]?.trim() || '',
            first_experience_date: firstExperienceTimestamp,
            motif_sortie: collab["motif_sortie"]?.trim() || '',
            comments: collab["comments"]?.trim() || '',
            etablissement: collab["etablissement"],
            nature_emploi: collab["nature_emploi"],
            statut_emploi: collab["statut_emploi"],
            position: collab["position"],
            coefficient: collab["coefficient"],
            current_team_name: collab["current_team_name"]?.trim() || '',
            current_localization: collab["current_localization"]?.trim() || '',
            current_poste: collab["current_poste"]?.trim() || '',
            current_id_team: collab["current_id_team"],

            school: collab["school"],
            school_contact: collab["school_contact"],
            diploma: collab["diploma"],
            training_cost: collab["training_cost"],
            remaining_cost: collab["remaining_cost"],
            tutor: collab["tutor"],

            date_fin_titre_sejour: titreDeSejourEndDate,

            last_contract_sortie_date: null,
            last_contract_entree_date: lastContractEntreeDateTimestamp,
            last_contract_type_contrat: collab["last_contract_type_contrat"] || null,

            last_team_name: collab["last_team_name"]?.trim() || '',
            last_localization: collab["last_localization"]?.trim() || '',
            last_poste: collab["last_poste"]?.trim() || '',
            last_id_team: collab["last_id_team"],

            // Flag, not the image — getAllCollabs returns every collab in one response, so
            // the picture itself is fetched per-collab from a cacheable URL. CollabAvatar
            // turns this into that URL; null means initials and no request.
            photoUpdatedAt: collab["photo_updated_at"]
          }

          if (lastContractSortieDateTimestamp
            && collab["last_contract_type_contrat"]
            && ['CDD', 'CDI', "Contrat d'apprentissage", 'Convention de stage'].includes(collab["last_contract_type_contrat"])
          ) {
            data.last_contract_sortie_date = lastContractSortieDateTimestamp
          }

          if (collab["id_team_delegate"]) {
            data.id_team_delegate = collab["id_team_delegate"];
          }
          collabsList.push(data)
        }

        state.allCollabsList = collabsList.sort(compareCollabsByOrder)
        state.allCollabsLoader = 'success'
      }
    });
    builder.addCase(resetPasswordCollab.fulfilled, (state) => {
      state.resetPasswordResponse = "success";
    })
    builder.addCase(resetPasswordCollab.rejected, (state, action) => {
      state.resetPasswordResponse = action.payload || "failed";
    })
    builder.addCase(fetchZipGenerationProgress.fulfilled, (state, action) => {
      if (action.payload && action.payload.progress !== undefined) {
        state.zipGenerationProgress = action.payload.progress;
      }
    });
    builder.addCase(getAllWarnings.fulfilled, (state, action) => {
      let warningsList = [];

      if (
        action.payload.responseData &&
        action.payload.responseData["warnings"]
      ) {
        for (const key in action.payload.responseData["warnings"]) {
          let warning = action.payload.responseData["warnings"][key];

          // Convert relevant date strings to timestamps
          let warningDateTimestamp = warning["warning_date"] ? moment(warning["warning_date"]).toDate() : null;
          let createdAtTimestamp = warning["created_at"] ? moment(warning["created_at"]).toDate() : null;
          let updatedAtTimestamp = warning["updated_at"] ? moment(warning["updated_at"]).toDate() : null;

          // Construct the data object for each warning
          let data = {
            id: warning.id,
            id_collab: warning.id_collab,
            category: warning.category ? warning.category.trim() : '',
            status: warning.status ? warning.status.trim() : '',
            warning_date: warningDateTimestamp,
            comments: warning.comments ? warning.comments : '',
            created_at: createdAtTimestamp,
            updated_at: updatedAtTimestamp,
            created_by: {
              id: warning.created_by.id,
              nom: warning.created_by.nom ? warning.created_by.nom.trim() : '',
              prenom: warning.created_by.prenom ? warning.created_by.prenom.trim() : ''
            },
            updated_by: {
              id: warning.updated_by.id,
              nom: warning.updated_by.nom ? warning.updated_by.nom.trim() : '',
              prenom: warning.updated_by.prenom ? warning.updated_by.prenom.trim() : ''
            },
            collab_nom: warning.collab_nom ? warning.collab_nom.trim() : '',
            collab_prenom: warning.collab_prenom ? warning.collab_prenom.trim() : '',
            collab_matricule: warning.collab_matricule ? warning.collab_matricule.trim() : '',
            collab_email: warning.collab_email ? warning.collab_email.trim() : '',
            poste: warning.poste ? warning.poste.trim() : '',
            team_name: warning.team_name ? warning.team_name.trim() : ''
          }

          let category = state.warningCatgories.filter(cat => cat.value == warning.category)
          if (category.length > 0) data.color = category[0].color

          warningsList.push(data)
        }

        state.warningsList = warningsList
      }
    });
    builder.addCase(deleteWarning.fulfilled, (state, action) => {
      if (!action.payload.responseError) {
        if (action.payload.responseData.status) {
          state.warningDeleteRes = action.payload.responseData.message
          state.warningsList = state.warningsList.filter(warning => warning.id !== action.payload.responseData.deleted_warning)
        }
      }
    })
    builder.addCase(editWarnings.fulfilled, (state, action) => {
      if (!action.payload.responseError) {
        if (action.payload.responseData.status) {
          state.warningEditRes = action.payload.responseData.message;

          state.warningsList = state.warningsList.map(warning => {
            if (warning.id === state.warningUpdateBody.warning_id) {
              const updatedWarning = { ...warning };
              if (state.warningUpdateBody.status !== undefined) {
                updatedWarning.status = state.warningUpdateBody.status;
              }
              if (state.warningUpdateBody.warning_date !== undefined) {
                updatedWarning.warning_date = new Date(state.warningUpdateBody.warning_date).getTime()
              }
              if (state.warningUpdateBody.comments !== undefined) {
                updatedWarning.comments = state.warningUpdateBody.comments;
              }

              return updatedWarning;
            }
            return warning;
          });
          state.warningUpdateBody = null;
        }
      }
    });
    builder.addCase(addWarning.fulfilled, (state, action) => {
      if (!action.payload.responseError) {
        if (action.payload.responseData.status) {
          state.addWarningRes = action.payload.responseData.message
          state.addedWarningBody = {
            ...state.addedWarningBody,
            id: action.payload.responseData.added_row_id
          };
          state.warningsList.push(state.addedWarningBody)
          state.addedWarningBody = null;
        } else if (!action.payload.responseData.status) {
          state.addWarningRes = action.payload.responseData.message
        }
      }
    })
    builder.addCase(getCompanies.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.companies) {
        state.companies = []
        for (const key in action.payload.responseData.companies) {
          let company = action.payload.responseData.companies[key]
          state.companies.push(
            {
              ...company,
              label: `${company.name} - ${company.localization}`,
              value: company.id
            }
          )
        }
      }
    });
    builder.addCase(getSingleCollabDetails.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.singleCollabDetails = action.payload.responseData.collab_data
        state.singleCollabProfile = action.payload.responseData.collab_profile
        state.singleCollabManagedTeams = action.payload.responseData.managed_teams
      }
    });
    builder.addCase(getPostes.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.postes) {
        state.postes = []
        for (const key in action.payload.responseData.postes) {
          let poste = action.payload.responseData.postes[key]
          state.postes.push(
            {
              ...poste,
              label: poste.name,
              value: poste.id
            }
          )
        }
      }
    });
    builder.addCase(addUser.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        state.addUserRes = action.payload.responseData.message;
      }
    });
    builder.addCase(getCollabContract.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData) {
        state.collabContract = action.payload.responseData;
      }
    });
    builder.addCase(editCollabContract.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status) {
        //state.editCollabContractRes = action.payload.responseData.message;
      }
    });
    builder.addCase(getSpecialCases.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.special_cases) {
        let casesList = action.payload.responseData.special_cases
        casesList.map(specialCase => {
          specialCase["startDate"] = specialCase.start_date ? moment(specialCase.start_date).toDate() : null
          specialCase["endDate"] = specialCase.end_date ? moment(specialCase.end_date).toDate() : null
        })
        state.specialCases = casesList
      }
    });
    builder.addCase(deleteCollabContract.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status == 1) {

      }
    });
    builder.addCase(getAllContracts.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status == 1) {
        state.allContracts = action.payload.responseData.contracts;
      }
    });
    builder.addCase(getAllMissionReviews.fulfilled, (state, action) => {
      if (action.payload.responseData && action.payload.responseData.status == 1) {
        state.allMissionsFollowups = action.payload.responseData.followups
      }
    });
  },
})

export const getCollabs = createAsyncThunk(
  "planning/getCollabs",
  async ({ selectedTeamsIds, isCurrent }, { getState }) => {
    try {
      let firstDayTimeStamp, lastDayTimeStamp
      if (isCurrent) {
        firstDayTimeStamp = moment().startOf("month").valueOf()
        lastDayTimeStamp = moment().endOf("month").valueOf()
      } else {
        const state = getState()
        const firstDay = new Date(state.planning.selectedDate)
        firstDayTimeStamp = firstDay.getTime()
        let lastDay = new Date(firstDayTimeStamp)
        if (state.selectedView === "WEEKLY") {
          lastDay.setDate(firstDay.getDate() + state.WEEKLY_VIEW_DAYS)
        } else {
          lastDay.setMonth(firstDay.getMonth() + 1)
        }
        lastDayTimeStamp = lastDay.getTime()
      }

      let body = {
        teamsIds: selectedTeamsIds,
        from: firstDayTimeStamp,
        to: lastDayTimeStamp
      }

      if (isCurrent) {
        body.isCurrent = isCurrent
      }

      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getCollabs,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)
export const getAllCollabs = createAsyncThunk(
  "planning/getAllCollabs",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const firstDay = new Date(state.planning.selectedDate)
      let firstDayTimeStamp = firstDay.getTime()
      let lastDay = new Date(firstDayTimeStamp)
      if (state.selectedView === "WEEKLY") {
        lastDay.setDate(firstDay.getDate() + state.WEEKLY_VIEW_DAYS)
      } else {
        lastDay.setMonth(firstDay.getMonth() + 1)
      }
      let body = {
        from: firstDayTimeStamp,
        to: lastDay.getTime()
      }
      store.dispatch(setIsLoading(true))
      const response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getAllCollabs,
        body
      )
      store.dispatch(setIsLoading(false))
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const resetPasswordCollab = createAsyncThunk(
  "planning/resetCollabPass",
  async (collabId) => {
    try {
      let body = {
        collabId: collabId
      }

      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.resetCollabPassword,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)

export const updateWeeklyReview = createAsyncThunk(
  "planning/updateWeeklyReview",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.updateWeeklyReview,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)

export const generateCraZip = createAsyncThunk(
  "planning/generateCraZip",
  async (body, thunkAPI) => {
    try {
      store.dispatch(setIsLoadingCra(true))
      let response = await NetworkManager.getInstance().fetchFile(
        config.apiPaths.planning.generateCraZip,
        body,
        body.fileName
      )
      store.dispatch(setIsLoadingCra(false))
      return response
    } catch (error) {
      store.dispatch(setIsLoadingCra(false))
      if (error.response && error.response.status === 423) {
        const errorMessage = error.response.data.error || "Génération d'un ZIP déjà en cours.";
        store.dispatch(showPopup({ show: true, title: "ERROR", message: errorMessage }))
      } else {
        store.dispatch(showPopup({ show: true, title: "ERROR", message: "Server error" }))
      }
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const generateCraPdf = createAsyncThunk(
  "planning/generateCraPdf",
  async (body, thunkAPI) => {
    try {
      store.dispatch(setIsLoading(true))
      let response = await NetworkManager.getInstance().fetchFile(
        config.apiPaths.planning.generateCraPdf,
        body,
        body.fileName
      )
      store.dispatch(setIsLoading(false))
      return response
    } catch (error) {
      store.dispatch(setIsLoading(false))
      if (error.message) {
        store.dispatch(showPopup({ show: true, title: "ERROR", message: error.message }))
      } else {
        store.dispatch(showPopup({ show: true, title: "ERROR", message: "Server error" }))
      }
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const fetchZipGenerationProgress = createAsyncThunk(
  "planning/fetchZipGenerationProgress",
  async (_, thunkAPI) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.zipGenerationProgress,
      )
      if (response && response.responseData) {
        return response.responseData;
      }
    } catch (error) {
      console.error("Error retrieving ZIP generation progress:", error);
      return thunkAPI.rejectWithValue(error);
    }
  }
)

// Fonction pour convertir une base64 en objet Blob
function base64ToBlob(base64) {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: 'application/zip' });
}

export const updateCollabs = createAsyncThunk(
  "planning/updateCollabs",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.updateCollabs,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)

export const mergeCollabs = createAsyncThunk(
  "planning/mergeCollabs",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.mergeCollabs,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)

/**
 * Upload a new picture for the signed-in collab.
 *
 * `image` is a base64 data URL of a 256x256 square, produced by ProfilePictureEditor. The
 * endpoint takes no collab id: the backend writes the session owner's row and nobody
 * else's, so there is nothing here to get wrong.
 */
/**
 * Removes another collab's picture. Requires `collabs` edit, enforced by the route guard.
 *
 * Takes an explicit id, unlike removeMyPhoto which derives the target from the session.
 * The id is echoed back in the payload so the reducer can patch the right row without
 * reading the store.
 */
export const removeCollabPhoto = createAsyncThunk(
  "collabs/removeCollabPhoto",
  async (idCollab) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.removeCollabPhoto,
        { id_collab: idCollab }
      )
      return { ...response, idCollab };
    } catch (error) {
      return { responseError: error, idCollab }
    }
  }
)

/**
 * Sets another collab's picture. Requires `collabs` edit, enforced by the route guard.
 *
 * Takes the id explicitly, unlike setMyPhoto which derives the target from the session, and
 * echoes it back so the reducer can patch the right row without reading the store.
 */
export const setCollabPhoto = createAsyncThunk(
  "collabs/setCollabPhoto",
  async ({ idCollab, image }) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.setCollabPhoto,
        { id_collab: idCollab, image }
      )
      return { ...response, idCollab };
    } catch (error) {
      return { responseError: error, idCollab }
    }
  }
)

export const setMyPhoto = createAsyncThunk(
  "collabs/setMyPhoto",
  async (image, { getState }) => {
    // Resolved here, not in the reducer: a reducer may not call store.getState(), and the
    // reducer needs to know which collab row to patch. `getState` inside a payload creator
    // is the sanctioned way to read it, and carrying the id in the payload keeps the
    // reducer a pure function of its action.
    const idCollab = getState().login.userId
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.setMyPhoto,
        { image }
      )
      return { ...response, idCollab };
    } catch (error) {
      return { responseError: error, idCollab }
    }
  }
)

export const removeMyPhoto = createAsyncThunk(
  "collabs/removeMyPhoto",
  async (_arg, { getState }) => {
    const idCollab = getState().login.userId
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.removeMyPhoto,
        {}
      )
      return { ...response, idCollab };
    } catch (error) {
      return { responseError: error, idCollab }
    }
  }
)

export const editCollab = createAsyncThunk(
  "collabs/editCollab",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.editCollab,
        body
      )
      return response;
    } catch (error) {
      return error
    }
  }
)

export const getMembers = createAsyncThunk(
  "planning/getMembers",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getMembers,
        body
      );
      return response;
    } catch (error) {
      return error
    }
  }
)

export const addGroupeMembers = createAsyncThunk(
  "planning/addGroupeMembers",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.addGroupeMembers,
        body
      )
      return response
    } catch (error) {
      return error
    }
  }
)

export const removeGroupeMembers = createAsyncThunk(
  "planning/removeGroupeMembers",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.removeGroupeMembers,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const getAllWarnings = createAsyncThunk(
  "planning/getWarnings",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getAllWarnings,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const deleteWarning = createAsyncThunk(
  "planning/deleteWarning",
  async (warningId) => {
    const body = { warning_id: warningId };
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.deleteWarning,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);
export const editWarnings = createAsyncThunk(
  "planning/editWarnings",
  async (warning) => {
    const body = warning;
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.editWarnings,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addWarning = createAsyncThunk(
  "planning/addWarning",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.addWarning,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
)

export const getCompanies = createAsyncThunk(
  "planning/getCompanies",
  async () => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getCompanies
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getSingleCollabDetails = createAsyncThunk(
  "planning/getSingleCollabDetails",
  async (collabId) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.getSingleCollabDetails,
        { id: collabId }
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getPostes = createAsyncThunk(
  "planning/getPostes",
  async () => {
    try {
      let response = await NetworkManager.getInstance().getRequest(
        config.apiPaths.planning.getPostes
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addUser = createAsyncThunk(
  "user/addUser",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.user.addUser,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addCollab = createAsyncThunk(
  "collabs/addCollab",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.addCollab,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getCollabContract = createAsyncThunk(
  "collabs/getCollabContract",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.getCollabContract,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addCollabContract = createAsyncThunk(
  "collabs/addCollabContract",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.addCollabContract,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const deleteCollabContract = createAsyncThunk(
  "collabs/deleteCollabContract",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.deleteCollabContract,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const editCollabContract = createAsyncThunk(
  "collabs/editCollabContract",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.editCollabContract,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getSpecialCases = createAsyncThunk(
  "planning/getSpecialCases",
  async () => {
    try {
      let response = await NetworkManager.getInstance().getRequest(
        config.apiPaths.planning.getSpecialCases
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addSpecialCase = createAsyncThunk(
  "planning/addSpecialCase",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.addSpecialCase,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const editSpecialCase = createAsyncThunk(
  "planning/editSpecialCase",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.planning.editSpecialCase,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const getAllContracts = createAsyncThunk(
  "collabs/getAllContracts",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.getAllContracts,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
)

export const getAllMissionReviews = createAsyncThunk(
  "collabs/getAllWeeklyReviews",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.collabs.getAllWeeklyReviews,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
)

export const typeOfContractOptions = [
  { label: 'CDI', value: 'CDI' },
  { label: 'Indépendant', value: 'Indépendant' },
  { label: 'Portage', value: 'Portage' },
  { label: 'Contrat d\'apprentissage', value: 'Contrat d\'apprentissage' },
  { label: 'Sous traitant', value: 'Sous traitant' },
  { label: 'Intérimaire', value: 'Intérimaire' },
  { label: 'CDD', value: 'CDD' },
  { label: 'Agence', value: 'Agence' },
  { label: 'Convention de stage', value: 'Convention de stage' }
]

export const contractsLocationsOptions = [
  { name: 'INSITU', label: 'INSITU', value: 'INSITU' },
  { name: 'MERIGNAC', label: 'MERIGNAC', value: 'MERIGNAC' },
  { name: 'LGC', label: 'LGC', value: 'LGC' },
  { name: 'GRADIGNAN', label: 'GRADIGNAN', value: 'GRADIGNAN' },
  { name: 'NEUILLY', label: 'NEUILLY', value: 'NEUILLY' },
  { name: 'INCONNU', label: 'INCONNU', value: 'INCONNU' },
  { name: 'BEYROUTH', label: 'BEYROUTH', value: 'BEYROUTH' },
  { name: 'ILE MAURICE', label: 'ILE MAURICE', value: 'ILE MAURICE' }
]

export const civiliteOptions = [
  { label: "Monsieur", value: "M." },
  { label: "Madame", value: "Mme" },
  { label: "Non renseigné", value: "Inconnu" }
]

export const cvSources = [
  { label: "Turnover-IT", value: "Turnover-IT" },
  { label: "Helloworks", value: "Helloworks" },
  { label: "Monster", value: "Monster" },
  { label: "Interim", value: "Interim" },
  { label: "Candidature spontanée", value: "Candidature spontanée" },
  { label: "Site internet", value: "Site internet" },
  { label: "Pôle Emploi", value: "Pôle Emploi" },
  { label: "Cooptation", value: "Cooptation" },
  { label: "Partenaire", value: "Partenaire" },
  { label: "Client", value: "Client" },
  { label: "Autre", value: "Autre" }
]

// the "did not start" case is represented twice on a contract: by the
// did_not_start switch and by this motif de sortie option
export const didNotStartMotif = "N'a pas démarré"

// keeps the two representations of "did not start" in sync: setting one of them
// fills the other only when it is still empty, so a value already chosen by the
// user is never overwritten by a change on another input. turning the switch on
// also dates the exit on the entry date, a contract that never started having
// no other exit date to offer
export function getDidNotStartSync(contract, key, value) {
  if (key === "did_not_start" && value === "yes") {
    const sync = {}
    if (!contract?.motif_sortie) {
      sync.motif_sortie = didNotStartMotif
    }
    if (!contract?.sortie_date && contract?.entree_date) {
      sync.sortie_date = moment(contract.entree_date).endOf("day").toDate()
    }
    return sync
  }
  if (key === "motif_sortie" && value === didNotStartMotif && !contract?.did_not_start) {
    return { did_not_start: "yes" }
  }
  return {}
}

export const optionsSortie = [
  { value: "Démission", label: "Démission" },
  { value: "Licenciement", label: "Licenciement" },
  { value: "Rupture conventionnelle", label: "Rupture conventionnelle" },
  { value: "Rupture anticipée du contrat", label: "Rupture anticipée du contrat" },
  { value: "Fin de CDD ", label: "Fin de CDD " },
  { value: "Fin de contrat d’apprentissage ", label: "Fin de contrat d’apprentissage " },
  { value: "Fin de stage ", label: "Fin de stage " },
  { value: "Fin de mission", label: "Fin de mission" },
  { value: didNotStartMotif, label: didNotStartMotif },
  { value: "Rupture de période d’essai employeur", label: "Rupture de période d’essai employeur" },
  { value: "Rupture de période d’essai salarié", label: "Rupture de période d’essai salarié" },
  { value: "Rupture décision administrative", label: "Rupture décision administrative" },
  { value: "Retraite", label: "Retraite" },
  { value: "Décès", label: "Décès" },
  { value: "Invalidité", label: "Invalidité" },
  { value: "Abandon de poste", label: "Abandon de poste" }
]

export const {
  setCollabs,
  setCurrentCollabs,
  setPhotoEditorOpen,
  setMyPhotoError,
  setWarningUpdateBody,
  setCollabModalData,
  setWarningDeleteRes,
  setWarningEditRes,
  setSelectedView,
  setResetPasswordModal,
  showCollabModal,
  setAddWarningDialog,
  setResetPasswordResponse,
  setWarningDetailsDialog,
  setWarningData,
  setUpdatedCurrentCollabs,
  addCollabToGroupe,
  setUpdateTeamPermissionsRes,
  setUpdatePermissionsRes,
  removeCollabFromGroupe,
  setCollabsGroupeId,
  setIsLoadingCra,
  setZipGenerationProgress,
  setAddWarningRes,
  setAddedWarningBody,
  addNewlyEvaluated,
  setCollabContract,
  setSpecialCases,
  setSpecialCaseModalData,
  setDisplaySpecialCaseModalDetails,
  setSingleCollabProfile,
  setSingleCollabManagedTeams,
  updateCollabSuiviStatus
} = collabsSlice.actions;

export default collabsSlice.reducer;
